import { THEMES, THEME_IDS, themeForGrade, type ThemeId } from '@numslearn/shared';
import { useSettings, type ThemeChoice } from '../settings.js';
import type { StringKey } from '../i18n/index.js';

/**
 * The theme switcher.
 *
 * Each card renders a real four-tile board painted with that theme's own
 * tokens in the brightness currently in force, so the choice is made by
 * looking at the thing itself rather than at a label.
 */
export function ThemePicker() {
  const { settings, set, mode, t, num } = useSettings();
  const choices: ThemeChoice[] = ['auto', ...THEME_IDS];
  const effective: ThemeId = settings.themeChoice === 'auto'
    ? themeForGrade(settings.grade)
    : (settings.themeChoice as ThemeId);

  return (
    <div className="theme-grid">
      {choices.map((choice) => {
        const id: ThemeId = choice === 'auto' ? themeForGrade(settings.grade) : choice;
        const theme = THEMES[id];
        const tk = theme[mode];
        const selected = settings.themeChoice === choice;
        const nameKey = (choice === 'auto' ? 'theme.auto' : theme.nameKey) as StringKey;
        const descKey = (choice === 'auto' ? 'theme.auto.desc' : theme.descKey) as StringKey;

        return (
          <button
            key={choice}
            type="button"
            className="theme-card"
            aria-pressed={selected}
            onClick={() => set('themeChoice', choice)}
          >
            <span
              className="theme-preview"
              data-art={theme.id}
              data-sel={theme.selectionStyle}
              style={{
                background: tk.bg,
                borderColor: tk.line,
                ['--g-ink' as string]: tk.ink,
                ['--g-tile' as string]: tk.tile,
                ['--g-tile-ink' as string]: tk.tileInk,
                ['--g-tile-radius' as string]: theme.tileRadius,
                ['--g-tile-shadow' as string]: tk.tileShadow,
                ['--g-c1' as string]: tk.c1,
                ['--g-c2' as string]: tk.c2,
                ['--g-c3' as string]: tk.c3,
                ['--g-c4' as string]: tk.c4,
                ['--g-weight' as string]: String(theme.weight),
                fontFamily: `${theme.fontLatin}, system-ui, sans-serif`,
              }}
              aria-hidden="true"
            >
              {[3, 7, 4, 6].map((n, i) => (
                <span
                  key={n}
                  className={`pv-tile${i === 1 ? ' is-sel' : ''}`}
                  style={{
                    ['--tile-c' as string]: theme.multicolourTiles
                      ? [tk.c1, tk.c2, tk.c3, tk.c4][i % 4]!
                      : tk.tile,
                    boxShadow: tk.tileShadow,
                  }}
                >
                  {num(n)}
                </span>
              ))}
            </span>
            <span className="theme-meta">
              <b>{t(nameKey)}</b>
              <small>{t(descKey)}</small>
              {choice === 'auto' && (
                <em className="theme-eff">→ {t(THEMES[effective].nameKey as StringKey)}</em>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
